export type AssetSceneKey =
  | "title"
  | "transition"
  | "map"
  | "ai"
  | "c1-observe"
  | "c1-classify"
  | "c1-sequence"
  | "c2-observe"
  | "c2-evidence"
  | "c3-latitude"
  | "c3-longitude"
  | "c3-elevation"
  | "c3-era"
  | "c3-evidence"
  | "c4-map"
  | "c4-match"
  | "c4-case"
  | "quiz-audit"
  | "report";

export type AssetRole =
  | "background"
  | "texture"
  | "mask"
  | "foreground"
  | "assistant"
  | "observation"
  | "card"
  | "evidence";

export type RuntimeImageFormat = "avif" | "webp" | "png";

interface AssetCatalogItem {
  id: string;
  scene: AssetSceneKey;
  role: AssetRole;
  description: string;
  alt?: string;
  width: number;
  height: number;
  alpha?: boolean;
}

export interface RuntimeImageSource {
  format: RuntimeImageFormat;
  src: string;
  width: number;
}

export interface AssetManifestEntry {
  id: AssetId;
  scene: AssetSceneKey;
  role: AssetRole;
  description: string;
  alt: string;
  sourcePath: string;
  width: number;
  height: number;
  aspectRatio: number;
  hasAlpha: boolean;
  preload: boolean;
  loading: "eager" | "lazy";
  sources: Partial<Record<RuntimeImageFormat, readonly RuntimeImageSource[]>>;
  fallbackSrc: string;
}

const assetCatalog = [
  { id: "1A-01", scene: "title", role: "background", description: "春季远山、河流、桃花和候鸟组成的桌面水墨长卷", width: 3840, height: 2160 },
  { id: "1A-02", scene: "title", role: "background", description: "春季远山、河流、桃树和候鸟组成的移动端竖版水墨长卷", width: 2160, height: 3840 },
  { id: "1B-01", scene: "transition", role: "texture", description: "用于页面转场的浅色宣纸纤维纹理", alt: "", width: 2048, height: 2048 },
  { id: "1B-02", scene: "transition", role: "mask", description: "用于纸层起伏和转场溶解的灰度高度图", alt: "", width: 2048, height: 2048 },
  { id: "1B-03", scene: "transition", role: "foreground", description: "转场左侧的多层宣纸切边", alt: "", width: 2048, height: 1024, alpha: true },
  { id: "1B-04", scene: "transition", role: "foreground", description: "转场右侧的多层宣纸切边", alt: "", width: 2048, height: 1024, alpha: true },
  { id: "1B-05", scene: "transition", role: "foreground", description: "转场下侧的多层宣纸切边", alt: "", width: 2048, height: 1024, alpha: true },
  { id: "1C-01", scene: "map", role: "background", description: "从春季河谷到农业生产区的四章旅程地图", width: 3840, height: 1440 },
  { id: "1D-01", scene: "ai", role: "assistant", description: "折纸种子形 AI 学习助手的待机状态", width: 1024, height: 1024, alpha: true },
  { id: "1D-02", scene: "ai", role: "assistant", description: "折纸种子形 AI 学习助手的观察状态", width: 1024, height: 1024, alpha: true },
  { id: "1D-03", scene: "ai", role: "assistant", description: "折纸种子形 AI 学习助手的提问状态", width: 1024, height: 1024, alpha: true },
  { id: "1D-04", scene: "ai", role: "assistant", description: "折纸种子形 AI 学习助手的证据核验状态", width: 1024, height: 1024, alpha: true },
  { id: "1D-05", scene: "ai", role: "assistant", description: "折纸种子形 AI 学习助手的离线状态", width: 1024, height: 1024, alpha: true },

  { id: "2A-01", scene: "c1-observe", role: "background", description: "冰雪消融、草木萌发和燕子归来的春季长卷", width: 1672, height: 941 },
  { id: "2A-02", scene: "c1-observe", role: "background", description: "草木繁盛、布谷鸟出现和果实孕育的夏季长卷", width: 1672, height: 941 },
  { id: "2A-03", scene: "c1-observe", role: "background", description: "果实成熟、树叶变黄和北雁南飞的秋季长卷", width: 1672, height: 941 },
  { id: "2A-04", scene: "c1-observe", role: "background", description: "衰草连天、昆虫匿迹和风雪将至的冬季长卷", width: 1672, height: 941 },
  { id: "2A-05", scene: "c1-observe", role: "foreground", description: "春景中归来的燕子前景切片", alt: "", width: 1200, height: 896, alpha: true },
  { id: "2A-06", scene: "c1-observe", role: "foreground", description: "夏景中的布谷鸟前景切片", alt: "", width: 1200, height: 896, alpha: true },
  { id: "2A-07", scene: "c1-observe", role: "foreground", description: "秋景中北雁南飞的前景切片", alt: "", width: 1392, height: 688, alpha: true },
  { id: "2A-08", scene: "c1-observe", role: "foreground", description: "春季飘落的桃花花瓣前景切片", alt: "", width: 992, height: 992, alpha: true },
  { id: "2A-09", scene: "c1-observe", role: "foreground", description: "秋季飘落的黄叶前景切片", alt: "", width: 992, height: 992, alpha: true },
  { id: "2A-10", scene: "c1-observe", role: "foreground", description: "用于季节观察与排序的成熟果实前景切片", alt: "", width: 1088, height: 896, alpha: true },

  { id: "2B-01", scene: "c1-classify", role: "card", description: "草木萌发物候现象卡片", width: 1200, height: 896, alpha: true },
  { id: "2B-02", scene: "c1-classify", role: "card", description: "桃花开放物候现象卡片", width: 1200, height: 896, alpha: true },
  { id: "2B-03", scene: "c1-classify", role: "card", description: "燕子归来物候现象卡片", width: 1200, height: 896, alpha: true },
  { id: "2B-04", scene: "c1-classify", role: "card", description: "布谷鸟到来物候现象卡片", width: 1200, height: 896, alpha: true },
  { id: "2B-05", scene: "c1-classify", role: "card", description: "果实成熟物候现象卡片", width: 1200, height: 896, alpha: true },
  { id: "2B-06", scene: "c1-classify", role: "card", description: "树叶变黄飘落物候现象卡片", width: 1200, height: 896, alpha: true },
  { id: "2B-07", scene: "c1-classify", role: "card", description: "北雁南飞物候现象卡片", width: 1200, height: 896, alpha: true },
  { id: "2B-08", scene: "c1-classify", role: "card", description: "昆虫销声匿迹物候现象卡片", width: 1200, height: 896, alpha: true },
  { id: "2B-09", scene: "c1-classify", role: "card", description: "冰雪融化季节气象背景卡片", width: 1200, height: 896, alpha: true },
  { id: "2B-10", scene: "c1-classify", role: "card", description: "风雪载途季节气象背景卡片", width: 1200, height: 896, alpha: true },
  { id: "2B-11", scene: "c1-classify", role: "card", description: "短时暴雨偶发自然事件卡片", width: 1200, height: 896, alpha: true },
  { id: "2B-12", scene: "c1-classify", role: "card", description: "山石滚落偶发自然事件卡片", width: 1200, height: 896, alpha: true },

  { id: "2C-01", scene: "c1-sequence", role: "card", description: "冰雪融化与草木萌发的四季时序卡片", width: 1392, height: 896, alpha: true },
  { id: "2C-02", scene: "c1-sequence", role: "card", description: "燕子归来的四季时序卡片", width: 1392, height: 896, alpha: true },
  { id: "2C-03", scene: "c1-sequence", role: "card", description: "布谷鸟与青果的四季时序卡片", width: 1392, height: 896, alpha: true },
  { id: "2C-04", scene: "c1-sequence", role: "card", description: "成熟果实与落叶的四季时序卡片", width: 1392, height: 896, alpha: true },
  { id: "2C-05", scene: "c1-sequence", role: "card", description: "四只斑头雁结队南飞的四季时序卡片", width: 1392, height: 896, alpha: true },
  { id: "2C-06", scene: "c1-sequence", role: "card", description: "衰草与风雪将至的四季时序卡片", width: 1392, height: 896, alpha: true },

  { id: "3A-01", scene: "c2-observe", role: "observation", description: "桃树休眠枝物候观察切片", width: 1600, height: 2000, alpha: true },
  { id: "3A-02", scene: "c2-observe", role: "observation", description: "桃树嫩芽萌发物候观察切片", width: 1600, height: 2000, alpha: true },
  { id: "3A-03", scene: "c2-observe", role: "observation", description: "桃树开花物候观察切片", width: 1600, height: 2000, alpha: true },
  { id: "3A-04", scene: "c2-observe", role: "observation", description: "桃树长叶物候观察切片", width: 1600, height: 2000, alpha: true },
  { id: "3B-01", scene: "c2-observe", role: "observation", description: "停栖燕子观察切片", width: 1200, height: 1200, alpha: true },
  { id: "3B-02", scene: "c2-observe", role: "observation", description: "平稳飞行燕子观察切片", width: 1200, height: 1200, alpha: true },
  { id: "3B-03", scene: "c2-observe", role: "observation", description: "从远处归来的燕子观察切片", width: 1200, height: 1200, alpha: true },
  { id: "3C-01", scene: "c2-evidence", role: "evidence", description: "比较生物物候记录与温湿度仪器的证据页", width: 2560, height: 1440 },

  { id: "4A-01", scene: "c3-latitude", role: "background", description: "比较南北地区春季物候差异的纬度实验双景", width: 3200, height: 1440 },
  { id: "4B-01", scene: "c3-longitude", role: "background", description: "比较沿海与内陆榆叶梅花期差异的海陆实验双景", width: 3200, height: 1440 },
  { id: "4C-01", scene: "c3-elevation", role: "background", description: "比较山脚、山腰与山顶物候差异的山地实验场景", width: 3840, height: 1920 },
  { id: "4D-01", scene: "c3-era", role: "background", description: "比较古今乔木抽青时间差异的年轮观察页", width: 3200, height: 1440 },
  { id: "4E-01", scene: "c3-evidence", role: "foreground", description: "纬度实验课文证据页装饰", alt: "", width: 1600, height: 992, alpha: true },
  { id: "4E-02", scene: "c3-evidence", role: "foreground", description: "经度与海陆差异实验课文证据页装饰", alt: "", width: 1600, height: 992, alpha: true },
  { id: "4E-03", scene: "c3-evidence", role: "foreground", description: "高下差异实验课文证据页装饰", alt: "", width: 1600, height: 992, alpha: true },
  { id: "4E-04", scene: "c3-evidence", role: "foreground", description: "古今差异实验课文证据页装饰", alt: "", width: 1600, height: 992, alpha: true },

  { id: "5A-01", scene: "c4-map", role: "background", description: "山谷农田与物候生产场景的横版背景", width: 1672, height: 941 },
  { id: "5A-02", scene: "c4-map", role: "observation", description: "依据物候选择播种时机的农田热点场景", width: 1600, height: 1200 },
  { id: "5A-03", scene: "c4-map", role: "observation", description: "依据物候资料安排作物区划的农田热点场景", width: 1600, height: 1200 },
  { id: "5A-04", scene: "c4-map", role: "observation", description: "依据物候安排造林与采种的林地热点场景", width: 1600, height: 1200 },
  { id: "5A-05", scene: "c4-map", role: "observation", description: "依据物候条件开展植物引种的观察圃热点场景", width: 1600, height: 1200 },
  { id: "5A-06", scene: "c4-map", role: "observation", description: "利用物候观察避免或减轻虫害的作物热点场景", width: 1600, height: 1200 },
  { id: "5A-07", scene: "c4-map", role: "observation", description: "调查山区气候和土壤适应情况的农业热点场景", width: 1600, height: 1200 },
  { id: "5B-01", scene: "c4-match", role: "card", description: "选择播种日期的农业生产用途卡片", width: 1200, height: 896, alpha: true },
  { id: "5B-02", scene: "c4-match", role: "card", description: "安排作物区划的农业生产用途卡片", width: 1200, height: 896, alpha: true },
  { id: "5B-03", scene: "c4-match", role: "card", description: "安排造林采种的农业生产用途卡片", width: 1200, height: 896, alpha: true },
  { id: "5B-04", scene: "c4-match", role: "card", description: "开展植物引种的农业生产用途卡片", width: 1200, height: 896, alpha: true },
  { id: "5B-05", scene: "c4-match", role: "card", description: "观察并防治虫害的农业生产用途卡片", width: 1200, height: 896, alpha: true },
  { id: "5B-06", scene: "c4-match", role: "card", description: "开展山区农业调查的生产用途卡片", width: 1200, height: 896, alpha: true },
  { id: "5C-01", scene: "c4-case", role: "background", description: "1962 年北京花生播种受低温影响案例的观察背景", width: 2560, height: 1440 },
  { id: "5C-02", scene: "c4-case", role: "card", description: "多种春季花木普遍晚开的观察卡", width: 1200, height: 896, alpha: true },
  { id: "5C-03", scene: "c4-case", role: "card", description: "多项物候记录汇入农业季节判断的观察卡", width: 1200, height: 896, alpha: true },
  { id: "5C-04", scene: "c4-case", role: "card", description: "仍按惯常时间播种花生的行动卡", width: 1200, height: 896, alpha: true },
  { id: "5C-05", scene: "c4-case", role: "card", description: "初生花生受早春低温影响的结果卡", width: 1200, height: 896, alpha: true },

  { id: "6A-01", scene: "quiz-audit", role: "background", description: "打开的观察册、物候证据卡和 AI 助手组成的草稿审校工作台", width: 2560, height: 1440 },
  { id: "6B-01", scene: "report", role: "foreground", description: "桃枝、花瓣、燕子和无字金色完成印记组成的桌面结课前景", width: 2560, height: 1440, alpha: true },
  { id: "6B-02", scene: "report", role: "foreground", description: "桃枝、花瓣、燕子和无字金色完成印记组成的移动端结课前景", width: 1440, height: 1920, alpha: true }
] as const satisfies readonly AssetCatalogItem[];

export type AssetId = (typeof assetCatalog)[number]["id"];

const namedSourceFiles: Partial<Record<AssetId, string>> = {
  "1A-01": "1A-01_序章_BG_桌面春季长卷.png",
  "1A-02": "1A-02_序章_BG_移动春季长卷.png",
  "1B-01": "1B-01_转场_RGB_宣纸纹理.png",
  "1B-02": "1B-02_转场_MASK_纸层高度.png",
  "1B-03": "1B-03_转场_FG_左侧纸边.png",
  "2A-01": "2A-01_v2-season-continuity.png",
  "2A-02": "2A-02_v2-season-continuity.png",
  "2A-03": "2A-03_v2-season-continuity.png",
  "2A-04": "2A-04_v3-season-continuity.png",
  "2A-08": "2A-08_api.png",
  "2A-10": "2A-10_v3-ripe-peaches.png",
  "2C-05": "2C-05_v6-geese.png",
  "3B-01": "3B-01_v2-barn-swallow.png",
  "4D-01": "4D-01_v2-controlled-era.png",
  "5B-03": "5B-03_v2-forestry-seeds.png",
  "5B-04": "5B-04_v3-plant-introduction.png",
  "6A-01": "6A-01_v2-seed-assistant-workbench.png",
  "1C-01": "1C-01_旅程地图_BG_底图.png",
  "6B-02": "6B-02_结课_FG_移动完成印记.png"
};

function widthsFor(id: AssetId, width: number, height: number): number[] {
  const requested = height > width ? [720] : [960];
  const widths = requested.filter((candidate) => candidate < width && candidate <= 1080);
  if (width <= 1080 || id === "5A-01") widths.push(width);
  return [...new Set(widths)]
    .sort((a, b) => a - b);
}

function runtimeSource(id: AssetId, width: number, format: RuntimeImageFormat): RuntimeImageSource {
  return {
    format,
    width,
    src: `https://assets.rsxr.eu.cc/nature/media/v1/images/${id}/${id}-${width}.${format}`
  };
}

function buildManifestEntry(item: AssetCatalogItem & { id: AssetId }): AssetManifestEntry {
  const hasAlpha = item.alpha === true;
  const widths = widthsFor(item.id, item.width, item.height);
  const webp = widths.map((width) => runtimeSource(item.id, width, "webp"));
  const png = hasAlpha ? [runtimeSource(item.id, Math.min(480, item.width), "png")] : undefined;
  const avif = hasAlpha ? undefined : widths.map((width) => runtimeSource(item.id, width, "avif"));
  const sourceFile = namedSourceFiles[item.id] ?? `${item.id}_generated.png`;

  return {
    id: item.id,
    scene: item.scene,
    role: item.role,
    description: item.description,
    alt: item.alt ?? item.description,
    sourcePath: `图片/${item.id}/${sourceFile}`,
    width: item.width,
    height: item.height,
    aspectRatio: item.width / item.height,
    hasAlpha,
    preload: item.id === "1A-01" || item.id === "1A-02",
    loading: item.id === "1A-01" || item.id === "1A-02" ? "eager" : "lazy",
    sources: { avif, webp, png },
    fallbackSrc: png?.[0].src ?? webp.at(-1)!.src
  };
}

export const assetIds = assetCatalog.map((item) => item.id) as readonly AssetId[];

/**
 * Source files stay in the catalog for traceability, but these IDs are no
 * longer published to the runtime media directory. Their scenes use the
 * procedural observation objects instead.
 */
export const retiredAssetIds = [
  "3B-02", "3B-03",
  "5C-01", "5C-02", "5C-03", "5C-04", "5C-05"
] as const satisfies readonly AssetId[];

export const activeAssetIds = assetIds.filter(
  (id): id is Exclude<AssetId, (typeof retiredAssetIds)[number]> => !retiredAssetIds.includes(id as (typeof retiredAssetIds)[number])
);

export const assetManifest = Object.fromEntries(
  assetCatalog.map((item) => [item.id, buildManifestEntry(item)])
) as Record<AssetId, AssetManifestEntry>;

export const backgroundMusicAsset = {
  id: "1A",
  src: "/media/v1/audio/nature-reply-loop.mp3",
  mimeType: "audio/mpeg",
  loopStartSeconds: 0.849,
  loopEndSeconds: 147.055,
  baseVolume: 0.24,
  duckedVolume: 0.15
} as const;

export function getAsset(id: AssetId): AssetManifestEntry {
  if (retiredAssetIds.includes(id as (typeof retiredAssetIds)[number])) {
    throw new Error(`Asset ${id} is retired and has no runtime media binding.`);
  }
  return assetManifest[id];
}

export function getAssetsForScene(scene: AssetSceneKey): AssetManifestEntry[] {
  return activeAssetIds.map((id) => assetManifest[id]).filter((asset) => asset.scene === scene);
}
